import { useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { UNSAFE_NavigationContext } from 'react-router-dom'; // only works for web platform
import UserContext from '../../context/UserContext';
import {useTranslation} from "react-i18next";

/**
 * This hook was placed back since <Prompt/> is temporarily removed from react-router-dom package
 */
function useBlocker(blocker, when = true) {
  const navigator = useContext(UNSAFE_NavigationContext).navigator;

  useEffect(() => {
    if (!when) return;

    const unblock = navigator.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        },
      }

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigator, blocker, when]);
}

/**
 * Currently the <Prompt> component is missing from the latest react-router-dom release
 * Will be added back once this feature is fully supported cross platform (web & native)
 * Replace this when this hook is re-added
 * https://github.com/remix-run/react-router/blob/abbea846b8cccc30b62aa7da9d13f3fabff6f980/docs/upgrading/v5.md#prompt-is-not-currently-supported
 */
function usePrompt(message, when = true) {
  let blocker = useCallback(
    tx => {
      if (window.confirm(message)) tx.retry();
    },
    [message]
  );

  useBlocker(blocker, when);
}

const PreventUnload = ({ children }) => {
  const { t } = useTranslation();
  const { changedDataWithoutSaving } = useContext(UserContext);
  const handleunload = useCallback(
    (e) => {
      if (changedDataWithoutSaving) {
        e.preventDefault();
        e.returnValue = '';
        return
      }
      return undefined;
    },
    [changedDataWithoutSaving]
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleunload);
    return () => window.removeEventListener('beforeunload', handleunload);
  }, [handleunload]);

  usePrompt(
    t('Je wijzigingen zijn niet opgeslagen. Weet je zeker dat je de pagina wilt sluiten?'),
    changedDataWithoutSaving
  );

  return (<>{children}</>);
};

PreventUnload.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

export default PreventUnload;
